import { billBandsFor } from "@medbillai/schemas";
import { isNateraExpressArm } from "@medbillai/schemas/nateraIntakePacket";

import type { FlowState } from "../../lib/flow-engine";
import { saveIntakeSession } from "./intakeSession";
import type {
  NateraIntakeAnswers,
  NateraIntakeConfig,
} from "./NateraIntakeFlow";
import {
  FEELINGS,
  type Feeling,
  STRESS_OPTIONS,
  type StressLevel,
} from "./persona";
import {
  BILL_SOURCES,
  type BillCount,
  type BillData,
  type BillSourceData,
  type EmotionalData,
  type LandingEntry,
} from "./types";

export const validationEntrySession = (
  entry: BillSourceData & EmotionalData,
  landingEntry: LandingEntry,
  previous: NateraIntakeAnswers = {},
): { state: FlowState<NateraIntakeConfig>; answers: NateraIntakeAnswers } => ({
  state: { tag: "PersonaValidation", data: entry },
  answers: {
    ...previous,
    // Control entry: a stale express arm reads as unenrolled.
    expressArm: isNateraExpressArm(previous.expressArm)
      ? null
      : previous.expressArm,
    stressLevel: entry.stressLevel,
    feeling: entry.feeling,
    landingEntry,
  },
});

export const seedIntakeSessionFromRouting = async (answers: {
  stressLevel: StressLevel;
  feeling: Feeling;
}): Promise<void> => {
  const { state, answers: seeded } = validationEntrySession(
    { billSource: "natera", ...answers },
    "routing",
  );
  await saveIntakeSession(state, seeded);
};

/** `?bill=natera&stress=a-lot&feeling=worried`; null unless all three are valid. */
export const landingAnswersFromUrl = (
  search: string,
): (BillSourceData & EmotionalData) | null => {
  const params = new URLSearchParams(search);
  const billSource = BILL_SOURCES.find(s => s === params.get("bill"));
  const stressLevel = STRESS_OPTIONS.find(
    ({ id }) => id === params.get("stress"),
  )?.level;
  const feeling = FEELINGS.find(({ id }) => id === params.get("feeling"))?.id;
  return billSource && stressLevel && feeling
    ? { billSource, stressLevel, feeling }
    : null;
};

const BILL_COUNTS: readonly BillCount[] = [1, 2, 3];

/**
 * `&count=2&band=0`, the bill answers from an off-site lander, seeding the amount
 * question. `band` indexes `billBandsFor(count)`, smallest first; unparseable
 * values are dropped.
 */
export const billAnswersFromUrl = (
  search: string,
): Partial<Pick<BillData, "billBand">> => {
  const params = new URLSearchParams(search);
  const billCount = BILL_COUNTS.find(
    count => String(count) === params.get("count"),
  );
  if (billCount === undefined) {
    return {};
  }
  const band = params.get("band");
  const billBand =
    band !== null && /^\d+$/.test(band)
      ? billBandsFor(billCount)[Number(band)]
      : undefined;
  return billBand === undefined ? {} : { billBand };
};
